import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Button, Spinner, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faMapMarkerAlt, faCrosshairs, faTimesCircle,
  faHandHoldingUsd, faFlag, faTrashAlt, faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../USER/Auth/AuthContext';
import { usePermissions } from '../../USER/Auth/PermissionsContext';
import { createService, getNearbyDrivers } from '../services/deliveryServices';
import ServiceMap from '../Map/ServiceMap';
import {
  calcularDistancia, estimarTiempo, calcularTarifa,
  getActiveTariffConfig, DEFAULT_CONFIG
} from '../utils/tarifaCalculator';
import { useExchangeRate } from '../utils/useExchangeRate';
import Swal from 'sweetalert2';
import { supabase } from '../../../supabaseClient';
import { getServiceCategories } from '../../../services/api/trips-ui';
import { notifyNewService } from '../../../services/pushNotifications';
import { VEHICLE_TYPES, SERVICE_STATUSES, SERVICE_TYPES, SEARCH_DECISION_WINDOW_SEC, DEFAULT_MAP_CENTER, PAYMENT_METHODS } from '../../../constants';
import { ScheduleRideModal } from './ScheduleRideModal';
import PriceCard from './components/PriceCard';
import SearchingDriversPanel from './components/SearchingDriversPanel';
import PriceNegotiation from './components/PriceNegotiation';
import ServiceRequestForm from './components/ServiceRequestForm';
import TripSummary from './components/TripSummary';
import PaymentSelector from './components/PaymentSelector';
import BryStepper from '../../UI/BryUI/BryStepper';
import { BryModal } from '../../UI/BryUI';
import PriceCalculator from '../Calculator/PriceCalculator';

const defaultCenter = { lat: DEFAULT_MAP_CENTER.lat, lng: DEFAULT_MAP_CENTER.lng };

function RequestService() {
  const { currentUser } = useAuth();
  const { can } = usePermissions();
  const rate = useExchangeRate();
  const [step, setStep] = useState(1);
  const [createdService, setCreatedService] = useState(null);
  const [serviceType, setServiceType] = useState(SERVICE_TYPES.RIDE);
  const [vehicleType, setVehicleType] = useState(VEHICLE_TYPES.MOTO);
  const [pesoKg, setPesoKg] = useState(0);

  const [pickupPos, setPickupPos] = useState(null);
  const [dropoffPos, setDropoffPos] = useState(null);

  const [pickupAddress, setPickupAddress] = useState('');
  const [dropoffAddress, setDropoffAddress] = useState('');

  const [distancia, setDistancia] = useState(0);
  const [tarifaResult, setTarifaResult] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.PAGO_MOVIL);
  const [proposePrice, setProposePrice] = useState(false);
  const [offeredPrice, setOfferedPrice] = useState('');
  const [passengerCount, setPassengerCount] = useState(1);
  const [referencePoints, setReferencePoints] = useState([]);
  const [refPointLabel, setRefPointLabel] = useState('');
  const [refPointMode, setRefPointMode] = useState(false);
  const [pendingRefPoint, setPendingRefPoint] = useState(null);

  // ── Config de tarifas desde BD ────────────────────────────────────────────
  const [tariffConfig, setTariffConfig] = useState(DEFAULT_CONFIG);
  const configRef = useRef(DEFAULT_CONFIG);

  const [loading, setLoading] = useState(false);
  const [nearbyDrivers, setNearbyDrivers] = useState([]);

  // ── Catálogo dinámico service_categories ─────────────────────────────────────
  const [catalog, setCatalog] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showPriceCalcModal, setShowPriceCalcModal] = useState(false);
  const [sheetCollapsed, setSheetCollapsed] = useState(false);
  const [decisionLeft, setDecisionLeft] = useState(0);

  // Cargar config de BD al montar
  useEffect(() => {
    getActiveTariffConfig().then(cfg => {
      setTariffConfig(cfg);
      configRef.current = cfg;
      if (pickupPos && dropoffPos) {
        const dist = calcularDistancia(pickupPos.lat, pickupPos.lng, dropoffPos.lat, dropoffPos.lng);
        const tiempo = estimarTiempo(dist);
        const tarifa = calcularTarifa(dist, pesoKg, tiempo, cfg, vehicleType, paymentMethod);
        setTarifaResult(tarifa);
      }
    });
  }, []);

  // ── Cargar catálogo de service_categories ──────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data: cats, error: catsError } = await getServiceCategories();
        if (catsError) throw catsError;
        if (cats && cats.length > 0) {
          setCatalog(cats);
          const first = cats[0];
          setSelectedCategory(first);
          setServiceType(first.slug);
          if (first.subcategories && first.subcategories.length > 0) {
            setSelectedSubcategory(first.subcategories[0]);
          }
        }
      } catch (e) {
        console.error('Error cargando catálogo:', e);
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setPickupPos(coords);
          loadNearbyDrivers(coords.lat, coords.lng);
        },
        () => {
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
              setPickupPos(coords);
              loadNearbyDrivers(coords.lat, coords.lng);
            },
            () => {
              setPickupPos(defaultCenter);
              loadNearbyDrivers(defaultCenter.lat, defaultCenter.lng);
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
          );
        },
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 15000 }
      );
    } else {
      setPickupPos(defaultCenter);
    }
  }, []);

  const loadNearbyDrivers = async (lat, lng) => {
    try {
      const drivers = await getNearbyDrivers(lat, lng, 5);
      setNearbyDrivers(drivers);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const channel = supabase
      .channel('public:drivers_tracking')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'drivers_tracking' },
        () => {
          if (pickupPos) {
            loadNearbyDrivers(pickupPos.lat, pickupPos.lng);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupPos]);

  // ── Cálculo dinámico con config de BD ────────────────────────────────────
  const updateDistancia = useCallback((pickup, dropoff, peso = pesoKg, vType = vehicleType) => {
    if (pickup && dropoff) {
      const dist = calcularDistancia(pickup.lat, pickup.lng, dropoff.lat, dropoff.lng);
      setDistancia(dist);
      const tiempo = estimarTiempo(dist);
      const tarifa = calcularTarifa(dist, peso, tiempo, configRef.current, vType);
      setTarifaResult(tarifa);
    } else {
      setDistancia(0);
      setTarifaResult(null);
    }
  }, [pesoKg, vehicleType]);

  const handlePickupChange = (coords) => {
    setPickupPos(coords);
    updateDistancia(coords, dropoffPos);
    reverseGeocode(coords.lat, coords.lng, setPickupAddress);
  };

  const handleDropoffChange = (coords) => {
    setDropoffPos(coords);
    updateDistancia(pickupPos, coords);
    reverseGeocode(coords.lat, coords.lng, setDropoffAddress);
    setSheetCollapsed(false);
  };

  const handlePesoChange = (newPeso) => {
    setPesoKg(newPeso);
    updateDistancia(pickupPos, dropoffPos, newPeso);
  };

  const handleServiceTypeChange = (tipo) => {
    setServiceType(tipo);
    const cat = catalog.find(c => c.slug === tipo);
    if (cat) {
      setSelectedCategory(cat);
      if (cat.subcategories && cat.subcategories.length > 0) {
        setSelectedSubcategory(cat.subcategories[0]);
      }
    }
    if (tipo === SERVICE_TYPES.RIDE) {
      handlePesoChange(0);
    } else {
      handlePesoChange(2);
    }
  };

  const reverseGeocode = async (lat, lng, setter) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=es`,
        { headers: { 'User-Agent': 'Bryemma/1.0' } }
      );
      const data = await res.json();
      if (data.display_name) {
        const parts = data.display_name.split(',');
        setter(parts.slice(0, 3).join(','));
      }
    } catch {
      setter(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    }
  };

  const handleAddressSearch = async (address, type) => {
    if (!address.trim()) return;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        { headers: { 'User-Agent': 'Bryemma/1.0' } }
      );
      const data = await res.json();
      if (data && data.length > 0) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        if (type === 'pickup') {
          setPickupPos(coords);
          updateDistancia(coords, dropoffPos);
          setPickupAddress(data[0].display_name.split(',').slice(0, 3).join(','));
        } else {
          setDropoffPos(coords);
          updateDistancia(pickupPos, coords);
          setDropoffAddress(data[0].display_name.split(',').slice(0, 3).join(','));
          setSheetCollapsed(false);
        }
      } else {
        Swal.fire('No encontrado', 'No pudimos ubicar esa dirección en el mapa.', 'warning');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error', 'Hubo un problema buscando la dirección.', 'error');
    }
  };

  const handleCalculateDistance = () => {
    if (pickupPos && dropoffPos) {
      setStep(2);
      setSheetCollapsed(false);
    } else {
      Swal.fire('Faltan datos', 'Toca el mapa para marcar origen y destino.', 'warning');
    }
  };

  const handleConfirmService = async () => {
    if (!currentUser || !tarifaResult) return;
    if (!can.createService()) {
      Swal.fire('Sin permisos', 'No tienes permisos para solicitar servicios.', 'error');
      return;
    }
    setLoading(true);

    try {
      const finalTarifa = calcularTarifa(distancia, pesoKg, estimarTiempo(distancia), configRef.current, vehicleType, paymentMethod);

      const payload = {
        user_uid: currentUser.id,
        type: serviceType,
        vehicle_type: vehicleType,
        status: SERVICE_STATUSES.SEARCHING,
        pickup: {
          address: pickupAddress || `${pickupPos.lat.toFixed(4)}, ${pickupPos.lng.toFixed(4)}`,
          lat: pickupPos.lat,
          lng: pickupPos.lng,
        },
        dropoff: {
          address: dropoffAddress || `${dropoffPos.lat.toFixed(4)}, ${dropoffPos.lng.toFixed(4)}`,
          lat: dropoffPos.lat,
          lng: dropoffPos.lng,
        },
        distance: distancia,
        price: parseFloat(finalTarifa.total),
        commission: parseFloat(finalTarifa.commission),
        payment_method: paymentMethod,
      };
      if (selectedSubcategory) {
        payload.subcategory_id = selectedSubcategory.id;
        payload.base_price = selectedSubcategory.base_price;
        payload.price_per_km = selectedSubcategory.price_per_km;
        payload.price_per_min = selectedSubcategory.price_per_min;
        payload.min_price = selectedSubcategory.min_price;
      }
      if (proposePrice && offeredPrice && parseFloat(offeredPrice) > 0) {
        payload.offered_price = parseFloat(offeredPrice);
      }
      if (passengerCount > 1) {
        payload.passenger_count = passengerCount;
      }
      if (referencePoints.length > 0) {
        payload.reference_points = referencePoints;
      }
      const newService = await createService(payload);
      setCreatedService(newService);
      if (newService) {
        notifyNewService(newService).catch((err) => {
          console.warn("Push notify drivers warning (silent):", err);
        });
      }
      setSheetCollapsed(false);
      setStep(3);
    } catch (error) {
      console.error("Error creating service:", error);
      Swal.fire('Error', `No se pudo crear el servicio. Detalles: ${error.message || JSON.stringify(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Ventana de decisión + cancelación de la búsqueda ──────────────────────
  useEffect(() => {
    if (!createdService) {
      setDecisionLeft(0);
      return undefined;
    }
    setDecisionLeft(SEARCH_DECISION_WINDOW_SEC);
    const id = setInterval(() => {
      setDecisionLeft(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [createdService]);

  const resetSearchState = () => {
    setStep(1);
    setCreatedService(null);
    setDecisionLeft(0);
    setDropoffPos(null);
    setTarifaResult(null);
    setProposePrice(false);
    setOfferedPrice('');
    setReferencePoints([]);
    setPendingRefPoint(null);
    setRefPointMode(false);
    setPassengerCount(1);
  };

  const cancelCreatedService = async () => {
    if (!createdService?.service_id) return;
    try {
      const { updateServiceStatus } = await import('../services/deliveryServices');
      await updateServiceStatus(createdService.service_id, SERVICE_STATUSES.CANCELLED);
      Swal.fire({ icon: 'info', title: 'Servicio cancelado', text: 'Tu solicitud fue cancelada.', confirmButtonColor: '#0EABB0' });
      resetSearchState();
    } catch (e) {
      console.error('Error cancelling:', e);
      Swal.fire({ icon: 'error', title: 'No se pudo cancelar', text: 'Intenta de nuevo en unos segundos.', confirmButtonColor: '#0EABB0' });
    }
  };

  const handleCancelSearch = async () => {
    if (!createdService) return;
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Cancelar el servicio?',
      text: 'Dejaremos de buscar conductores para esta solicitud.',
      showCancelButton: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'Seguir esperando',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#0EABB0',
    });
    if (result.isConfirmed) {
      await cancelCreatedService();
    }
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) {
      Swal.fire('GPS no disponible', 'Tu navegador no soporta geolocalización.', 'warning');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        handlePickupChange(coords);
      },
      (err) => {
        if (err.code === 1) {
          Swal.fire('Permiso de ubicación', 'Activa los permisos de ubicación en tu navegador para usar tu posición GPS.', 'warning');
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            handlePickupChange(coords);
          },
          () => {
            Swal.fire('Señal GPS débil', 'No se pudo obtener tu ubicación exacta. Puedes tocar el mapa para marcar el punto de recogida.', 'info');
          },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
        );
      },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 15000 }
    );
  };

  const handleSwapPickupDropoff = () => {
    const tempPos = pickupPos;
    const tempAddr = pickupAddress;
    setPickupPos(dropoffPos);
    setPickupAddress(dropoffAddress);
    setDropoffPos(tempPos);
    setDropoffAddress(tempAddr);
    updateDistancia(dropoffPos, tempPos);
  };

  return (
    <div style={{ height: 'calc(100dvh - 56px)', minHeight: 'calc(100dvh - 56px)', maxHeight: 'calc(100dvh - 56px)', width: '100%', position: 'relative', overflow: 'hidden', backgroundColor: 'var(--bry-bg-body)' }}>
      {/* ── MAPA GLOBAL DE FONDO ── */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        {step !== 3 ? (
          <ServiceMap
            pickupPos={pickupPos}
            dropoffPos={dropoffPos}
            onPickupChange={handlePickupChange}
            onDropoffChange={handleDropoffChange}
            drivers={nearbyDrivers}
            height="100%"
            refPoints={referencePoints}
            onRefPointAdd={(coords) => {
              setPendingRefPoint(coords);
              setRefPointLabel('');
            }}
            refPointMode={refPointMode}
          />
        ) : (
          <ServiceMap
            pickupPos={{ lat: createdService?.pickup?.lat, lng: createdService?.pickup?.lng }}
            dropoffPos={{ lat: createdService?.dropoff?.lat, lng: createdService?.dropoff?.lng }}
            drivers={nearbyDrivers}
            height="100%"
            interactive={false}
          />
        )}
      </div>

      {/* ── HEADER FLOATING (STEPPER) ── */}
      <div style={{ position: 'absolute', top: 12, left: 16, right: 16, zIndex: 10 }}>
        <BryStepper steps={['Ruta', 'Resumen', 'Buscando']} currentStep={step} />
      </div>

      {/* ── MAP CONTROLS (FLOATING OVER MAP) ── */}
      {step === 1 && (
        <div style={{ position: 'absolute', right: 16, top: 64, zIndex: 10, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button variant="light" className="rounded-circle shadow-sm" style={{ width: 44, height: 44 }} onClick={handleLocateMe} title="Usar mi ubicación GPS">
            <FontAwesomeIcon icon={faCrosshairs} color="var(--bry-primary)" />
          </Button>
          {pickupPos && dropoffPos && (
            <Button variant="light" className="rounded-circle shadow-sm" style={{ width: 44, height: 44 }} onClick={handleSwapPickupDropoff} title="Invertir origen y destino">
              <FontAwesomeIcon icon={faMapMarkerAlt} color="var(--bry-primary)" />
            </Button>
          )}
          <Button variant={refPointMode ? "dark" : "light"} className="rounded-circle shadow-sm" style={{ width: 44, height: 44 }} onClick={() => setRefPointMode(!refPointMode)} title="Agregar punto de referencia">
            <FontAwesomeIcon icon={faFlag} color={refPointMode ? "#fff" : "var(--bry-primary)"} />
          </Button>
        </div>
      )}

      {/* ── REFERENCIAS (FLOATING BUBBLES) ── */}
      {step === 1 && referencePoints.length > 0 && (
        <div style={{ position: 'absolute', top: 64, left: 16, right: 72, zIndex: 10, display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px' }}>
          {referencePoints.map((rp, i) => (
            <Badge key={i} pill bg="white" text="dark" className="shadow-sm d-flex align-items-center gap-2 p-2 border">
              <FontAwesomeIcon icon={faFlag} color="#2196F3" />
              {rp.label || `Punto ${i + 1}`}
              <FontAwesomeIcon
                icon={faTrashAlt}
                style={{ cursor: 'pointer', color: '#999' }}
                onClick={() => setReferencePoints(prev => prev.filter((_, idx) => idx !== i))}
              />
            </Badge>
          ))}
        </div>
      )}

      {/* ── BUBBLE PENDING REF POINT ── */}
      {step === 1 && pendingRefPoint && (
        <div style={{ position: 'absolute', top: 64, left: 16, right: 72, zIndex: 10 }}>
          <Card className="shadow-lg border-0 rounded-16">
            <Card.Body className="p-2 d-flex gap-2">
              <Form.Control
                size="sm"
                placeholder="Nombre del punto..."
                value={refPointLabel}
                onChange={(e) => setRefPointLabel(e.target.value)}
                className="rounded-pill border-0 bg-light"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && refPointLabel.trim()) {
                    setReferencePoints(prev => [...prev, { ...pendingRefPoint, label: refPointLabel.trim() }]);
                    setPendingRefPoint(null);
                    setRefPointLabel('');
                    setRefPointMode(false);
                  }
                }}
              />
              <Button
                size="sm"
                variant="primary"
                className="rounded-pill px-3"
                style={{ background: 'var(--bry-primary)' }}
                disabled={!refPointLabel.trim()}
                onClick={() => {
                  setReferencePoints(prev => [...prev, { ...pendingRefPoint, label: refPointLabel.trim() }]);
                  setPendingRefPoint(null);
                  setRefPointLabel('');
                  setRefPointMode(false);
                }}
              >
                OK
              </Button>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* ── BOTTOM SHEET (PANEL INTERACTIVO) ── */}
      <motion.div
        className="position-absolute bottom-0 w-100"
        style={{
          backgroundColor: 'var(--bry-bg-primary, #ffffff)',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          zIndex: 1000,
          maxHeight: sheetCollapsed ? '140px' : 'calc(100dvh - 116px)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'max-height 0.25s ease'
        }}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Indicador para minimizar/expandir panel y ver el mapa */}
        <div
          className="d-flex flex-column align-items-center py-2"
          style={{ cursor: 'pointer', flexShrink: 0 }}
          onClick={() => setSheetCollapsed(prev => !prev)}
          title={sheetCollapsed ? "Expandir formulario" : "Minimizar para ver el mapa"}
        >
          <div style={{ width: '44px', height: '5px', backgroundColor: 'var(--bry-accent, #0EABB0)', opacity: 0.7, borderRadius: '999px' }} />
          <small className="text-muted mt-1" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
            {sheetCollapsed ? 'Toca para abrir formulario ▲' : 'Toca aquí para ver más mapa ▼'}
          </small>
        </div>

        {/* Área de scroll interno para el contenido de los pasos */}
        <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y', flex: 1, minHeight: 0, padding: step === 3 ? '0 14px calc(100px + env(safe-area-inset-bottom, 0px)) 14px' : '0 14px 12px 14px', overscrollBehavior: 'contain' }}>
          {step === 1 && (
            <ServiceRequestForm
              catalogLoading={catalogLoading}
              catalog={catalog}
              serviceType={serviceType}
              handleServiceTypeChange={handleServiceTypeChange}
              vehicleType={vehicleType}
              setVehicleType={setVehicleType}
              updateDistancia={updateDistancia}
              pickupPos={pickupPos}
              dropoffPos={dropoffPos}
              pesoKg={pesoKg}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              setSelectedSubcategory={setSelectedSubcategory}
              handlePesoChange={handlePesoChange}
              tariffConfig={tariffConfig}
              passengerCount={passengerCount}
              setPassengerCount={setPassengerCount}
              handleLocateMe={handleLocateMe}
              handleSwapPickupDropoff={handleSwapPickupDropoff}
              pickupAddress={pickupAddress}
              dropoffAddress={dropoffAddress}
              setPickupAddress={setPickupAddress}
              setDropoffAddress={setDropoffAddress}
              handleAddressSearch={handleAddressSearch}
              nearbyDrivers={nearbyDrivers}
              referencePoints={referencePoints}
              setReferencePoints={setReferencePoints}
              pendingRefPoint={pendingRefPoint}
              setPendingRefPoint={setPendingRefPoint}
              refPointLabel={refPointLabel}
              setRefPointLabel={setRefPointLabel}
              refPointMode={refPointMode}
              setRefPointMode={setRefPointMode}
              distancia={distancia}
              tarifaResult={tarifaResult}
              handleCalculateDistance={handleCalculateDistance}
              handlePickupChange={handlePickupChange}
              handleDropoffChange={handleDropoffChange}
              onOpenCalculator={() => setShowPriceCalcModal(true)}
            />
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <TripSummary 
                pickupAddress={pickupAddress} 
                dropoffAddress={dropoffAddress}
                distancia={distancia}
                passengerCount={passengerCount}
                referencePoints={referencePoints}
              />
              <PriceCard tarifa={tarifaResult} rate={rate} />
              <PriceNegotiation
                proposePrice={proposePrice}
                setProposePrice={setProposePrice}
                offeredPrice={offeredPrice}
                setOfferedPrice={setOfferedPrice}
                tarifaResult={tarifaResult}
              />
              <PaymentSelector paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} />
            </motion.div>
          )}

          {/* ── Step 3: Buscando conductor ────────────────────────────────────── */}
          {step === 3 && createdService && (
            <SearchingDriversPanel
              service={createdService}
              nearbyDrivers={nearbyDrivers}
              onDriverFound={resetSearchState}
              onCancel={cancelCreatedService}
            />
          )}
        </div>

        {/* ── BARRA FIJA INFERIOR PARA STEP 1 (CONTINUAR / VER TARIFA SIEMPRE VISIBLE) ── */}
        {step === 1 && (
          <div
            className="px-3 py-2.5 border-top d-flex align-items-center shadow-lg"
            style={{
              flexShrink: 0,
              backgroundColor: 'var(--bry-bg-card, #FFFFFF)',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              zIndex: 100
            }}
          >
            <Button
              variant="primary"
              size="lg"
              aria-label={pickupPos && dropoffPos ? `Ver Tarifa y Continuar al resumen — $${tarifaResult?.total || '...'}` : 'Ver Tarifa — Marca origen y destino en el mapa'}
              className="w-100 rounded-pill py-2.5 border-0 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
              style={{ background: '#0EABB0', color: '#FFFFFF', minHeight: '48px', fontSize: '0.96rem' }}
              onClick={handleCalculateDistance}
              disabled={!pickupPos || !dropoffPos || catalogLoading || !selectedCategory}
            >
              {pickupPos && dropoffPos ? (
                <>
                  <span>Continuar al resumen (Ver Tarifa) — ${tarifaResult?.total || '...'}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              ) : (
                <span>Marca origen y destino en el mapa (Ver Tarifa)</span>
              )}
            </Button>
          </div>
        )}

        {/* ── BARRA FIJA INFERIOR PARA STEP 2 (CONFIRMAR SERVICIO SIEMPRE VISIBLE) ── */}
        {step === 2 && (
          <div
            className="px-3 py-2.5 border-top d-flex gap-2 align-items-center shadow-lg"
            style={{
              flexShrink: 0,
              backgroundColor: 'var(--bry-bg-card, #FFFFFF)',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
              zIndex: 100
            }}
          >
            <Button
              variant="light"
              onClick={() => setStep(1)}
              className="rounded-pill py-2.5 px-3 fw-bold border-0"
              style={{ backgroundColor: '#F1F5F9', color: '#334155', minHeight: '48px', minWidth: '80px' }}
            >
              Volver
            </Button>
            <Button
              variant="outline-primary"
              className="rounded-pill py-2.5 px-3 fw-bold"
              style={{ border: '2px solid #0EABB0', color: '#0EABB0', background: 'transparent', minHeight: '48px' }}
              onClick={() => setShowScheduleModal(true)}
            >
              Programar
            </Button>
            <Button
              variant="primary"
              aria-label="Confirmar y Solicitar Servicio"
              className="flex-fill rounded-pill py-2.5 px-3 border-0 fw-bold shadow-sm"
              style={{ background: '#0EABB0', color: '#ffffff', fontSize: '0.96rem', minHeight: '48px' }}
              onClick={handleConfirmService}
              disabled={loading || !tarifaResult}
            >
              {loading ? (
                <Spinner size="sm" />
              ) : proposePrice && parseFloat(offeredPrice) > 0 ? (
                <><FontAwesomeIcon icon={faHandHoldingUsd} className="me-1" /> Solicitar Oferta (${parseFloat(offeredPrice).toFixed(2)})</>
              ) : (
                <><FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Confirmar / Solicitar (${tarifaResult?.total || '0.00'})</>
              )}
            </Button>
          </div>
        )}
      </motion.div>

      {/* ── BARRA DE DECISIÓN: CANCELAR DURANTE LA BÚSQUEDA ── */}
      {step === 3 && createdService && (
        <div
          className="d-flex align-items-center gap-2"
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
            zIndex: 1040,
            padding: '10px 12px',
            borderRadius: 16,
            backgroundColor: 'var(--bry-bg-card, #ffffff)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.18)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B' }}>
              {decisionLeft > 0 ? 'Tiempo para decidir' : 'Buscando conductor'}
            </span>
            <span style={{ display: 'block', fontSize: decisionLeft > 0 ? 20 : 13, fontWeight: 900, lineHeight: 1.2, color: decisionLeft > 0 ? '#0F172A' : '#C2410C', fontVariantNumeric: 'tabular-nums' }}>
              {decisionLeft > 0
                ? `${String(Math.floor(decisionLeft / 60)).padStart(2, '0')}:${String(decisionLeft % 60).padStart(2, '0')}`
                : 'Puedes cancelar mientras esperas'}
            </span>
          </div>
          <Button
            variant="danger"
            className="rounded-pill border-0 fw-bold d-flex align-items-center"
            style={{ backgroundColor: '#EF4444', minHeight: 44, padding: '0 18px', fontSize: '0.9rem', whiteSpace: 'nowrap' }}
            onClick={handleCancelSearch}
          >
            <FontAwesomeIcon icon={faTimesCircle} className="me-2" /> Cancelar
          </Button>
        </div>
      )}

      <ScheduleRideModal
        show={showScheduleModal}
        onHide={() => setShowScheduleModal(false)}
        passengerUid={currentUser?.id}
        isVip={false}
        pickupAddress={pickupAddress || ''}
        pickupLat={pickupPos?.lat || 0}
        pickupLng={pickupPos?.lng || 0}
        dropoffAddress={dropoffAddress || ''}
        dropoffLat={dropoffPos?.lat || 0}
        dropoffLng={dropoffPos?.lng || 0}
        referencePoint={referencePoints.length > 0 ? referencePoints[referencePoints.length - 1] : null}
        serviceType={serviceType || 'ride'}
      />

      {/* Modal Interactivo de Calculadora */}
      <BryModal
        isOpen={showPriceCalcModal}
        onClose={() => setShowPriceCalcModal(false)}
        title="Calculadora de Tarifa"
        size="md"
      >
        <PriceCalculator
          distanciaInicial={distancia}
          vehicleTypeInicial={vehicleType}
          serviceTypeInicial={serviceType}
          onTarifaCalculada={(res) => {
            setTarifaResult(res);
          }}
          onConfirmarTarifa={(res) => {
            setTarifaResult(res);
            setShowPriceCalcModal(false);
            if (pickupPos && dropoffPos) {
              setStep(2);
            }
          }}
        />
      </BryModal>
    </div>
  );
}

export default RequestService;
